// Step1.jsx
import React, { useEffect, useState } from "react";
import {
  TextField,
  InputLabel,
  Box,
  Typography,
  RadioGroup,
  FormControl,
  FormControlLabel,
  Radio,
  FormLabel,
  Button,
  InputAdornment,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import { Formik, Form } from "formik";
import * as Yup from "yup";
import { uploadImage } from "services/imageUpload";
import { IMAGE_URL } from "network";
import { generateStrongPassword } from "utility";

function Step1({ initialData, onNext }) {
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    if (initialData?.image) {
      setImagePreview(IMAGE_URL + initialData.image);
    }
  }, [initialData?.image]);

  const [showPassword, setShowPassword] = useState(false);

  console.log(" initialData.image", initialData.image);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    phone: Yup.string()
      .required("Phone is required")
      .matches(/^[6-9]\d{9}$/, "Enter a valid 10-digit mobile number"),
    gender: Yup.string().required("Gender is required"),
    image: Yup.string().required("Image is required"),
    password: initialData?._id
      ? Yup.string() // update mode → optional
      : Yup.string() // create mode → required
          .required("Password is required")
          .min(8, "Password must be at least 8 characters")
          .matches(/[a-z]/, "At least one lowercase letter required")
          .matches(/[A-Z]/, "At least one uppercase letter required")
          .matches(/[0-9]/, "At least one number required")
          .matches(/[@$!%*?&#]/, "At least one special character required"),
  });

  const initialValues = {
    name: initialData?.name || "",
    email: initialData?.email || "",
    phone: initialData?.phone || "",
    gender: initialData?.gender || "male",
    image: initialData?.image || "",
    password: "", // always empty on edit
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        // setSubmitting(true);
        // onNext(values);
        // setSubmitting(false);
        setSubmitting(true);

        const payload = { ...values };

        // If in update mode and password is empty → don't send it
        if (initialData?._id && !payload.password) {
          delete payload.password;
        }

        onNext(payload);
        setSubmitting(false);
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        setFieldValue,
        isSubmitting,
      }) => (
        <Form>
          <Box className="container py-4">
            <Typography variant="h5" gutterBottom>
              Staff Details
            </Typography>
            <div className="row">
              <div className="col-md-6 mb-4">
                <InputLabel className="mb-2">Profile Image</InputLabel>
                <Box
                  sx={{
                    width: 150,
                    height: 150,
                    border: "2px dashed #ccc",
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    cursor: "pointer",
                    overflow: "hidden",
                    backgroundColor: "#f9f9f9",
                  }}
                  onClick={() =>
                    document.getElementById("profile-image-input").click()
                  }
                >
                  {imagePreview ? (
                    <img
                      crossOrigin="anonymous"
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <AddIcon fontSize="large" color="action" />
                  )}
                  <input
                    type="file"
                    id="profile-image-input"
                    hidden
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const uploadedPath = await uploadImage(file);
                        setFieldValue("image", uploadedPath);
                        setImagePreview(IMAGE_URL + uploadedPath);
                      }
                    }}
                  />
                </Box>
                {touched.image && errors.image && (
                  <Typography color="error" variant="caption">
                    {errors.image}
                  </Typography>
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-4">
                <TextField
                  label="Staff Name"
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  fullWidth
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                />
              </div>

              <div className="col-md-6 mb-4">
                <TextField
                  label="Email"
                  type="email"
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  fullWidth
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                />
              </div>

              <div className="col-md-6 mb-4">
                <TextField
                  label="Mobile"
                  type="tel"
                  name="phone"
                  value={values.phone}
                  onChange={handleChange}
                  fullWidth
                  error={touched.phone && Boolean(errors.phone)}
                  helperText={touched.phone && errors.phone}
                />
              </div>

              <div className="col-md-6 mb-4">
                <FormControl fullWidth>
                  <FormLabel>Gender</FormLabel>
                  <RadioGroup
                    row
                    name="gender"
                    value={values.gender}
                    onChange={handleChange}
                  >
                    <FormControlLabel
                      value="male"
                      control={<Radio />}
                      label="Male"
                    />
                    <FormControlLabel
                      value="female"
                      control={<Radio />}
                      label="Female"
                    />
                    <FormControlLabel
                      value="other"
                      control={<Radio />}
                      label="Other"
                    />
                  </RadioGroup>
                  {touched.gender && errors.gender && (
                    <Typography color="error" variant="caption">
                      {errors.gender}
                    </Typography>
                  )}
                </FormControl>
              </div>

              <div className="col-md-6 mb-4">
                <TextField
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={values.password}
                  onChange={handleChange}
                  fullWidth
                  error={touched.password && Boolean(errors.password)}
                  helperText={touched.password && errors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((prev) => !prev)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                        <Button
                          onClick={() => {
                            const strongPassword = generateStrongPassword();
                            setFieldValue("password", strongPassword);
                            setShowPassword(true);
                          }}
                          size="small"
                          sx={{ ml: 1 }}
                        >
                          Generate
                        </Button>
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
            </div>

            <Box display="flex" justifyContent="flex-end" mt={3}>
              <Button type="submit" variant="contained" disabled={isSubmitting}>
                Next
              </Button>
            </Box>
          </Box>
        </Form>
      )}
    </Formik>
  );
}

export default Step1;
